import { useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogBody,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Loader2, CheckCircle2, XCircle } from 'lucide-react';
import { toast } from '@/components/ui/use-toast';
import api from '@/lib/api';
import type { BetaAccessRequestResponse } from '@/lib/api';

interface AdminRequestDetailProps {
  request: BetaAccessRequestResponse | null;
  open: boolean;
  onClose: () => void;
  onUpdated: () => void;
}

export function AdminRequestDetail({ request, open, onClose, onUpdated }: AdminRequestDetailProps) {
  const [adminNotes, setAdminNotes] = useState('');
  const [isUpdating, setIsUpdating] = useState(false);

  if (!request) return null;

  async function handleAction(status: 'accepted' | 'rejected') {
    if (!request) return;

    setIsUpdating(true);
    try {
      await api.updateBetaAccessRequestStatus(request.requestId, {
        status,
        adminNotes: adminNotes.trim() || undefined,
      });

      toast({
        description: `Request ${status === 'accepted' ? 'accepted' : 'rejected'} successfully.`,
      });

      setAdminNotes('');
      onUpdated();
      onClose();
    } catch (err) {
      toast({
        description: err instanceof Error ? err.message : 'Failed to update request',
      });
    } finally {
      setIsUpdating(false);
    }
  }

  const isPending = request.status === 'pending';

  return (
    <Dialog open={open} onOpenChange={(isOpen) => !isOpen && onClose()}>
      <DialogContent className="max-w-lg max-h-[85vh] flex flex-col">
        <DialogHeader>
          <DialogTitle>Request Detail</DialogTitle>
        </DialogHeader>

        <DialogBody>
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <p className="text-sm text-muted-foreground">Name</p>
                <p className="font-medium">{request.name}</p>
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Status</p>
                <Badge
                  variant={
                    request.status === 'accepted'
                      ? 'default'
                      : request.status === 'rejected'
                        ? 'destructive'
                        : 'secondary'
                  }
                >
                  {request.status}
                </Badge>
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Email</p>
                <p className="font-medium">{request.email}</p>
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Telegram</p>
                <p className="font-medium">{request.telegram || '—'}</p>
              </div>
            </div>

            <div>
              <p className="text-sm text-muted-foreground">Description</p>
              <p className="mt-1 text-sm whitespace-pre-wrap">{request.description}</p>
            </div>

            {request.accessCode && (
              <div>
                <p className="text-sm text-muted-foreground">Access Code</p>
                <code className="text-sm font-mono bg-muted px-2 py-1 rounded">
                  {request.accessCode}
                </code>
              </div>
            )}

            {request.adminNotes && (
              <div>
                <p className="text-sm text-muted-foreground">Admin Notes</p>
                <p className="mt-1 text-sm">{request.adminNotes}</p>
              </div>
            )}

            <div className="text-xs text-muted-foreground">
              Submitted: {new Date(request.createdAt).toLocaleString()}
              {request.ipAddress && <> &middot; IP: {request.ipAddress}</>}
            </div>

            {isPending && (
              <div className="space-y-2">
                <Label htmlFor="admin-notes">Admin Notes (optional)</Label>
                <Textarea
                  id="admin-notes"
                  value={adminNotes}
                  onChange={(e) => setAdminNotes(e.target.value)}
                  placeholder="Internal notes about this decision..."
                  rows={2}
                  maxLength={2000}
                  disabled={isUpdating}
                />
              </div>
            )}
          </div>

          {isPending && (
            <DialogFooter className="gap-2">
              <Button
                variant="destructive"
                onClick={() => handleAction('rejected')}
                disabled={isUpdating}
              >
                {isUpdating ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  <XCircle className="h-4 w-4 mr-1" />
                )}
                Reject
              </Button>
              <Button onClick={() => handleAction('accepted')} disabled={isUpdating}>
                {isUpdating ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  <CheckCircle2 className="h-4 w-4 mr-1" />
                )}
                Accept
              </Button>
            </DialogFooter>
          )}
        </DialogBody>
      </DialogContent>
    </Dialog>
  );
}
